/**
 * ExternalImageWidget component.
 * @module components/Widgets/ExternalImage
 */
import React, { useState } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormFieldWrapper, Icon } from '@plone/volto/components';
import { addAppURL, isInternalURL, flattenToAppURL, URLUtils } from '@plone/volto/helpers';
import clearSVG from '@plone/volto/icons/clear.svg';
import { Input, Button, Image } from 'semantic-ui-react';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import withObjectBrowser from '../../editor/plugins/AlfrescolinkPlugin/Sidebar/ObjectBrowser';

/** Widget to edit urls
 *
 * This is the default widget used for the `remoteUrl` field. You can also use
 * it by declaring a field like:
 *
 * ```jsx
 * {
 *  title: "URL",
 *  widget: 'url',
 * }
 * ```
 */
const ImageInputWidget = (props) => {
  const { id, onChange, onBlur, onClick, minLength, maxLength, placeholder, isDisabled } = props;
  const inputId = `field-${id}`;
  const rawValue = props.value;
  const [value, setValue] = useState(flattenToAppURL(rawValue));
  const [isInvalid, setIsInvalid] = useState(false);
  /**
   * Clear handler
   * @method clear
   * @param {Object} value Value
   * @returns {undefined}
   */
  const clear = () => {
    setValue('');
    onChange(id, undefined);
  };

  const onChangeValue = (_value) => {
    let newValue = _value;
    if (newValue?.length > 0) {
      if (isInvalid && URLUtils.isUrl(URLUtils.normalizeUrl(newValue))) {
        setIsInvalid(false);
      }

      if (isInternalURL(newValue)) {
        newValue = flattenToAppURL(newValue);
      }
    }

    setValue(newValue);

    newValue = isInternalURL(newValue) ? addAppURL(newValue) : newValue;

    if (!isInternalURL(newValue) && newValue.length > 0) {
      const checkedURL = URLUtils.checkAndNormalizeUrl(newValue);
      newValue = checkedURL.url;
      if (!checkedURL.isValid) {
        setIsInvalid(true);
      }
    }

    onChange(id, newValue === '' ? undefined : newValue);
    props.onValueChange && props.onValueChange(newValue);
  };

  return (
    <FormFieldWrapper {...props} className="url wide">
      <div className="wrapper">
        {!value && (
          <Input
            id={inputId}
            name={id}
            type="url"
            value={value || ''}
            disabled={isDisabled}
            placeholder={placeholder}
            onChange={({ target }) => onChangeValue(target.value)}
            onBlur={({ target }) => onBlur(id, target.value === '' ? undefined : target.value)}
            onClick={() => onClick()}
            minLength={minLength || null}
            maxLength={maxLength || null}
            error={isInvalid}
          />
        )}

        {value?.length > 0 ? (
          <Button.Group>
            <Image src={value} size={'tiny'} />
            <Button
              basic
              className="cancel"
              aria-label="clearUrlBrowser"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
            >
              <Icon name={clearSVG} size="30px" />
            </Button>
          </Button.Group>
        ) : (
          <Button.Group>
            <Button
              basic
              icon
              aria-label="openUrlBrowser"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.openObjectBrowser({
                  mode: 'link',
                  overlay: true,
                  selectableTypes: ['image/jpeg', 'image/png', 'image/gif'],
                  currentPath: 'd74ba8e9-4ee1-4d03-b9e7-4b8f0f571886',
                  onSelectItem: (url) => {
                    onChangeValue(url);
                  },
                });
              }}
            >
              <Icon name={navTreeSVG} size="24px" />
            </Button>
          </Button.Group>
        )}
      </div>
    </FormFieldWrapper>
  );
};

/**
 * Property types
 * @property {Object} propTypes Property types.
 * @static
 */
ImageInputWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  openObjectBrowser: PropTypes.func.isRequired,
  onValueChange: PropTypes.func,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
ImageInputWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: () => {},
  onBlur: () => {},
  onClick: () => {},
  minLength: null,
  maxLength: null,
  onValueChange: () => {},
};

export default compose(withObjectBrowser)(ImageInputWidget);
